import { useState, forwardRef, useImperativeHandle, useMemo } from 'react';
import { Form, Row, Col } from 'react-bootstrap';
import provinceCantonData from '../../data/ecuador_provinces_cantons_labeled.json';
import { useSelector } from 'react-redux';
import { selectIdIsForeigner, selectIdPersonType, selectIdType } from '../../slices/idSlice';
import DocIdGroup from '../user/DocIdGroup';
import { setFinalDocId } from '../../utils/docIdUtils';
import { useGetShippingCitiesQuery } from '../../slices/shippingApiSlice';
import { toast } from 'react-toastify';
import { sanitizeInput } from '../../utils/formUtils';
import SearchableSelect from '../form/SearchableSelect';
import useIsMobile from '../../utils/useIsMobile';
import Loader from '../layout/Loader';

const AddressEditForm = forwardRef(({
    onSubmit,
    onCancel,
    initialData = {},
    userInfo,
    useFor = 'address',
}, ref) => {
    const idTypeSelect = useSelector(selectIdType);
    const idPersonTypeSelect = useSelector(selectIdPersonType);
    const idIsForeignerSelect = useSelector(selectIdIsForeigner);
    const isMobile = useIsMobile();

    const { data: remoteCities } = useGetShippingCitiesQuery();

    // Transform remote cities array into the same structure as local JSON
    const activeProvinceCantonData = useMemo(() => {
        if (!remoteCities || remoteCities.length === 0) {
            return provinceCantonData;
        }

        // Group cities by province
        const transformed = {};
        remoteCities.forEach(item => {
            const { province, city, label } = item;
            if (!transformed[province]) {
                transformed[province] = [];
            }
            transformed[province].push({ [city]: label });
        });

        return transformed;
    }, [remoteCities]);

    // Prepare provinces array for SearchableSelect
    const provinces = useMemo(() =>
        Object.keys(activeProvinceCantonData).sort(),
        [activeProvinceCantonData]
    );

    const [docId, setDocId] = useState(userInfo?.taxDocId || initialData.docId || userInfo.docId || '');
    const [validDocId, setValidDocId] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [type, setType] = useState(initialData.type || idPersonTypeSelect);
    const [taxDocId, setTaxDocId] = useState(initialData.taxDocId || '');
    const [isForeigner, setIsForeigner] = useState(initialData.isForeigner ?? idIsForeignerSelect);
    const isCreatingAddress = useFor === 'address' && !initialData?._id;

    const [formData, setFormData] = useState({
        address1: '',
        address2: '',
        number: '',
        reference: '',
        state: '',
        city: '',
        label: '',
        phoneNumber: '',
        name: userInfo.name,
        email: userInfo.email || '',
        ...initialData,
    });

    useImperativeHandle(ref, () => ({
        wipeForm
    }));

    const cantons = useMemo(() => {
        return formData.state ? activeProvinceCantonData[formData.state] || [] : [];
    }, [formData.state, activeProvinceCantonData]);

    // Prepare cantons data with metadata for SearchableSelect
    const cantonsWithMetadata = useMemo(() => {
        return cantons.map((cantonObj) => {
            const [cantonName, label] = Object.entries(cantonObj)[0];
            return {
                name: cantonName,
                label: label,
                value: `${cantonName}|${label}`,
                disabled: label === 'NA'
            };
        });
    }, [cantons]);

    const fillForm = (cPerson) => {
        setFormData((prev) => ({
            ...prev,
            name: cPerson[0].nombre_comercial || cPerson[0].razon_social,
            email: cPerson[0].email,
            address1: cPerson[0].direccion,
        }));
        setType(cPerson[0].tipo);
        cPerson[0].ruc && setTaxDocId(cPerson[0].ruc);
        cPerson[0].es_extranjero && setIsForeigner(cPerson[0].es_extranjero);
    };

    const wipeForm = () => {
        setDocId('');
        setTaxDocId('');
        setType(idPersonTypeSelect);
        setIsForeigner(idIsForeignerSelect);
        setFormData({
            address1: '',
            address2: '',
            number: '',
            reference: '',
            state: '',
            city: '',
            label: '',
            phoneNumber: '',
            name: '',
            email: '',
        });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        const customType = e.target.getAttribute('type');

        if (name === 'city') {
            const [city, label] = value.split('|');
            setFormData((prev) => ({
                ...prev,
                city,
                label,
            }));
        } else {
            const sanitizedValue = sanitizeInput(value, customType);

            setFormData((prev) => ({
                ...prev,
                [name]: sanitizedValue,
                ...(name === 'state' ? { city: '', label: '' } : {}),
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isSubmitting) return;

        if (!validDocId) {
            toast.error('Identificación Inválida');
            return;
        }

        if (useFor === 'address' && !formData.state) {
            toast.error('Seleccione una provincia');
            return;
        }

        if (useFor === 'address' && !formData.city) {
            toast.error('Seleccione una ciudad o cantón');
            return;
        }

        const result = setFinalDocId(idTypeSelect, type, docId);
        if (!result) return;
        const { finalDocId, finalType } = result;

        setIsSubmitting(true);
        try {
            await Promise.resolve(onSubmit({
                ...formData,
                docId: finalDocId,
                taxDocId,
                type: finalType,
            }));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="address-form-wrapper">
            <Form id="address-edit-form" onSubmit={handleSubmit} className="p-1">
                <fieldset disabled={isSubmitting}>
                <div className="bg-light p-3 rounded shadow-sm border">
                    <h6 className="mb-3 border-bottom pb-2 text-primary">Información de Identificación</h6>
                    <Row>
                        <Col xs={12}>
                            <DocIdGroup
                                docId={docId}
                                setDocId={setDocId}
                                type={type}
                                setType={setType}
                                taxDocId={taxDocId}
                                setTaxDocId={setTaxDocId}
                                isForeigner={isForeigner}
                                setIsForeigner={setIsForeigner}
                                fillForm={fillForm}
                                setValidDocId={setValidDocId}
                            />
                        </Col>
                    </Row>
                </div>

                <div className="bg-white p-3 rounded mb-4 shadow-sm border">
                    <h6 className="mb-3 border-bottom pb-2 text-primary">Datos de Contacto</h6>
                    <Row>
                        <Col md={6}>
                            <Form.Group controlId="name" className="mb-3">
                                <Form.Label column='sm' className="small fw-bold">Nombre</Form.Label>
                                <Form.Control
                                    name="name"
                                    type="text"
                                    required
                                    placeholder="Nombre completo"
                                    value={formData.name}
                                    onChange={handleChange}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group controlId="email" className="mb-3">
                                <Form.Label column='sm' className="small fw-bold">Email</Form.Label>
                                <Form.Control
                                    name="email"
                                    type="email"
                                    required
                                    placeholder="correo@ejemplo.com"
                                    value={formData.email}
                                    onChange={handleChange}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group controlId="phoneNumber" className="mb-3">
                                <Form.Label column='sm' className="small fw-bold">Teléfono</Form.Label>
                                <Form.Control
                                    name="phoneNumber"
                                    type="numeric"
                                    required
                                    placeholder="0999999999"
                                    value={formData.phoneNumber}
                                    onChange={handleChange}
                                />
                            </Form.Group>
                        </Col>
                        {useFor === 'billing' && (
                            <Col md={6}>
                                <Form.Group controlId="address1" className="mb-3">
                                    <Form.Label column='sm' className="small fw-bold">Dirección</Form.Label>
                                    <Form.Control
                                        name="address1"
                                        type="alphanumeric"
                                        required
                                        value={formData.address1}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </Col>
                        )}
                    </Row>
                </div>

                <div className="bg-white p-3 rounded mb-4 shadow-sm border" hidden={useFor === 'billing'}>
                    <h6 className="mb-3 border-bottom pb-2 text-primary">Dirección de Entrega</h6>
                    <Row>
                        <Col md={8}>
                            <Form.Group controlId="address1" className="mb-3">
                                <Form.Label column='sm' className="small fw-bold">
                                    Calle Principal
                                </Form.Label>
                                <Form.Control
                                    name="address1"
                                    type="alphanumeric"
                                    required={useFor === 'address'}
                                    value={formData.address1}
                                    onChange={handleChange}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group controlId="number" className="mb-3">
                                <Form.Label column='sm' className="small fw-bold">Número</Form.Label>
                                <Form.Control
                                    name="number"
                                    type="alphanumeric"
                                    required={useFor === 'address'}
                                    value={formData.number}
                                    onChange={handleChange}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={12}>
                            <Form.Group controlId="address2" className="mb-3">
                                <Form.Label column='sm' className="small fw-bold">Calle Secundaria</Form.Label>
                                <Form.Control
                                    name="address2"
                                    type="alphanumeric"
                                    required={useFor === 'address'}
                                    value={formData.address2}
                                    onChange={handleChange}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={12}>
                            <Form.Group controlId="reference" className="mb-3">
                                <Form.Label column='sm' className="small fw-bold">Referencia</Form.Label>
                                <Form.Control
                                    name="reference"
                                    type="alphanumeric"
                                    required={useFor === 'address'}
                                    placeholder="Ej: Frente al parque, casa color verde..."
                                    value={formData.reference}
                                    onChange={handleChange}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group controlId="state" className="mb-3">
                                {isMobile ? (
                                    <SearchableSelect
                                        label="Provincia"
                                        name="state"
                                        options={provinces}
                                        value={formData.state}
                                        onChange={handleChange}
                                        placeholder="Seleccione provincia"
                                        searchPlaceholder="Buscar provincia..."
                                        required={false}
                                    />
                                ) : (
                                    <>
                                        <Form.Label column='sm' className="small fw-bold">Provincia</Form.Label>
                                        <Form.Select
                                            name="state"
                                            required={useFor === 'address'}
                                            value={formData.state}
                                            onChange={handleChange}
                                        >
                                            <option value="">Seleccione provincia</option>
                                            {provinces.map((prov) => (
                                                <option key={prov} value={prov}>
                                                    {prov}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </>
                                )}
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group controlId="city" className="mb-3">
                                {isMobile ? (
                                    <SearchableSelect
                                        label="Ciudad/Cantón"
                                        name="city"
                                        options={cantonsWithMetadata}
                                        value={`${formData.city}|${formData.label}`}
                                        onChange={handleChange}
                                        placeholder="Seleccione cantón"
                                        searchPlaceholder="Buscar cantón..."
                                        required={false}
                                        disabled={!formData.state}
                                        getOptionLabel={(option) => option.name}
                                        getOptionValue={(option) => option.value}
                                        isOptionDisabled={(option) => option.disabled}
                                        renderOption={(option) => (
                                            <>
                                                {option.name} {option.disabled && '(No disponible)'}
                                            </>
                                        )}
                                    />
                                ) : (
                                    <>
                                        <Form.Label column='sm' className="small fw-bold">Ciudad/Cantón</Form.Label>
                                        <Form.Select
                                            name="city"
                                            required={useFor === 'address'}
                                            value={`${formData.city}|${formData.label}`}
                                            onChange={handleChange}
                                            disabled={!formData.state}
                                        >
                                            <option value="">Seleccione cantón</option>
                                            {cantons.map((cantonObj, idx) => {
                                                const [cantonName, label] = Object.entries(cantonObj)[0];
                                                return (
                                                    <option
                                                        key={idx}
                                                        value={`${cantonName}|${label}`}
                                                        disabled={label === 'NA'}
                                                    >
                                                        {cantonName} {label === 'NA' && '(No disponible)'}
                                                    </option>
                                                );
                                            })}
                                        </Form.Select>
                                    </>
                                )}
                            </Form.Group>
                        </Col>
                    </Row>
                </div>
                </fieldset>

                {isCreatingAddress && isSubmitting && (
                    <div className="text-center mb-3">
                        <Loader />
                        <div className="small text-muted mt-2">Creando dirección...</div>
                    </div>
                )}

            </Form>
        </div>
    );
});

export default AddressEditForm;
